/* ============================================================
   crest.js - behaviours for the Crest Racing restyle.
   Mirrors the interaction set observed on mxbolts.us:
   announcement rotator, hide-on-scroll header, drag rails with
   arrows/dots/counter, tabbed panels, comparison tooltip, mobile nav.
   Deliberately NOT included: live viewer counter, countdown timer.
   ============================================================ */
(function () {
  'use strict';

  var quickAddBound = false;

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* ---------- 1. Announcement rotator ---------- */
  function initAnnouncement(root) {
    var inner = root.querySelector('.abar__slides-inner');
    if (!inner || inner.dataset.crestReady === '1') return;
    var slides = inner.children;
    if (slides.length < 2) return;
    inner.dataset.crestReady = '1';

    var i = 0;
    var timer = null;

    // Slide width is 640px on desktop and 100% of the track on mobile, so measure
    // it rather than translating by a percentage of the (much wider) inner element.
    function go(n) {
      i = (n + slides.length) % slides.length;
      var w = slides[0].getBoundingClientRect().width;
      inner.style.transform = 'translateX(' + (-i * w) + 'px)';
    }
    function start() { stop(); timer = setInterval(function () { go(i + 1); }, 4000); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    root.querySelectorAll('[data-abar-prev]').forEach(function (b) {
      b.addEventListener('click', function () { go(i - 1); start(); });
    });
    root.querySelectorAll('[data-abar-next]').forEach(function (b) {
      b.addEventListener('click', function () { go(i + 1); start(); });
    });
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    window.addEventListener('resize', function () { go(i); });

    go(0);
    start();
  }

  /* ---------- 2. Hide-on-scroll header ---------- */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header || header.dataset.crestReady === '1') return;
    header.dataset.crestReady = '1';
    var last = window.pageYOffset;
    var threshold = 120;
    window.addEventListener('scroll', function () {
      var y = window.pageYOffset;
      if (y > last && y > threshold) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
      last = y;
    }, { passive: true });
  }

  /* ---------- 3. Rails (drag, arrows, dots, counter) ---------- */
  function initRail(scope) {
    var rail = scope.querySelector('[data-rail]');
    if (!rail || rail.dataset.crestRailReady === '1') return;
    rail.dataset.crestRailReady = '1';

    var itemSel = rail.getAttribute('data-rail-item') || '> *';
    var prev = scope.querySelector('[data-rail-prev]');
    var next = scope.querySelector('[data-rail-next]');
    var dotWrap = scope.querySelector('[data-rail-dots]');
    var cur = scope.querySelector('[data-rail-cur]');
    var tot = scope.querySelector('[data-rail-tot]');

    function items() { return Array.prototype.slice.call(rail.querySelectorAll(itemSel)); }
    function step() {
      var it = items();
      if (!it.length) return rail.clientWidth;
      var cs = getComputedStyle(rail);
      var gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
      return it[0].getBoundingClientRect().width + gap;
    }
    function index() { var s = step(); return s ? Math.round(rail.scrollLeft / s) : 0; }
    function pages() {
      var s = step();
      if (!s) return 1;
      return Math.max(1, Math.ceil((rail.scrollWidth - rail.clientWidth) / s) + 1);
    }

    function sync() {
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      if (prev) prev.classList.toggle('is-disabled', atStart);
      if (next) next.classList.toggle('is-disabled', atEnd);
      var idx = index();
      if (cur) cur.textContent = String(Math.min(idx + 1, items().length));
      if (tot) tot.textContent = String(items().length);
      if (dotWrap) {
        var dots = dotWrap.children;
        for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('is-active', d === idx);
      }
    }

    function buildDots() {
      if (!dotWrap) return;
      dotWrap.innerHTML = '';
      var n = pages();
      if (n < 2) return;
      for (var d = 0; d < n; d++) {
        (function (d) {
          var b = document.createElement('button');
          b.className = 'collgrid__dot' + (d === 0 ? ' is-active' : '');
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (d + 1));
          b.addEventListener('click', function () {
            rail.scrollTo({ left: d * step(), behavior: 'smooth' });
          });
          dotWrap.appendChild(b);
        })(d);
      }
    }

    if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });

    var down = false, startX = 0, startScroll = 0, moved = false;
    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      down = true; moved = false;
      startX = e.clientX; startScroll = rail.scrollLeft;
      rail.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      rail.scrollLeft = startScroll - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!down) return;
      down = false;
      rail.classList.remove('is-dragging');
    });
    rail.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);

    rail.addEventListener('scroll', function () { window.requestAnimationFrame(sync); }, { passive: true });
    window.addEventListener('resize', function () { buildDots(); sync(); });

    buildDots();
    sync();
  }

  /* ---------- 4. Tabs ---------- */
  function initTabs(root) {
    var nav = root.querySelector('[data-tabs-nav]');
    if (!nav || nav.dataset.crestReady === '1') return;
    nav.dataset.crestReady = '1';
    nav.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tab-index]');
      if (!btn) return;
      var idx = btn.getAttribute('data-tab-index');
      nav.querySelectorAll('[data-tab-index]').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
      root.querySelectorAll('[data-tab-panel]').forEach(function (p) {
        var on = p.getAttribute('data-tab-panel') === idx;
        p.classList.toggle('is-active', on);
        if (on) initRail(p);
      });
    });
  }

  /* ---------- 5. Comparison tooltip ---------- */
  function initTooltip() {
    var tip = document.getElementById('ticomp-tip');
    if (!tip) return;
    document.querySelectorAll('.ticomp__row[data-tip]').forEach(function (row) {
      if (row.dataset.crestReady === '1') return;
      row.dataset.crestReady = '1';
      row.addEventListener('mouseenter', function () {
        var title = row.getAttribute('data-tip-title') || '';
        tip.innerHTML = (title ? '<strong>' + title + '</strong>' : '') + row.getAttribute('data-tip');
        var r = row.getBoundingClientRect();
        tip.style.left = Math.max(12, r.left) + 'px';
        tip.style.top = (r.top - tip.offsetHeight - 12) + 'px';
        tip.classList.add('is-visible');
      });
      row.addEventListener('mouseleave', function () { tip.classList.remove('is-visible'); });
    });
  }

  /* ---------- 6. Mobile nav ---------- */
  function initMobileNav() {
    var nav = document.getElementById('mobile-nav');
    var overlay = document.getElementById('mobile-nav-overlay');
    if (!nav || nav.dataset.crestReady === '1') return;
    nav.dataset.crestReady = '1';

    function open() {
      nav.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      nav.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    document.querySelectorAll('[data-mobile-nav-open]').forEach(function (b) { b.addEventListener('click', open); });
    document.querySelectorAll('[data-mobile-nav-close]').forEach(function (b) { b.addEventListener('click', close); });
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ---------- 7. Quick add from product cards ---------- */
  function initQuickAdd() {
    // NOTE: this flag is a closure variable, NOT a data attribute on <body>.
    // It used to be `document.body.dataset.crestQuickAdd = '1'`, which writes
    // data-crest-quick-add onto <body> - the exact attribute this handler
    // selects on. closest('[data-crest-quick-add]') then matched <body> for
    // every click on the page, so preventDefault() killed every link on the
    // whole site. Do not reintroduce a DOM flag whose name collides with a
    // selector used anywhere in this file.
    if (quickAddBound) return;
    quickAddBound = true;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-crest-quick-add]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-crest-quick-add');
      if (!id) return;
      btn.disabled = true;
      fetch(shopRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (r) { return r.json(); })
        .then(function () { document.dispatchEvent(new CustomEvent('crest:cart:changed')); })
        .catch(function () {})
        .finally(function () { btn.disabled = false; });
    });

    document.addEventListener('crest:cart:changed', function () {
      fetch(shopRoot() + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('.site-header__cartcount, .stickycart__count').forEach(function (el) {
            el.textContent = cart.item_count;
            el.setAttribute('data-count', cart.item_count);
          });
        })
        .catch(function () {});
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.abar').forEach(initAnnouncement);
    initHeader();
    (scope || document).querySelectorAll('[data-crest-rail-section]').forEach(function (s) {
      if (!s.classList.contains('topsell__panel') || s.classList.contains('is-active')) initRail(s);
    });
    (scope || document).querySelectorAll('[data-crest-tabs]').forEach(initTabs);
    initTooltip();
    initMobileNav();
    initQuickAdd();
  }

  ready(function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
